import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  OrganizationInterface,
  OrganizationsByRoleInterface,
  ReviewInterface,
} from '@sport-activities/sdk';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { StatfulReviewFiltersInterface } from '~/core/interfaces/statful-review-filters.interface';
import { UserService } from '~/auth/services/user/user.service';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import { createHttpParams } from '~/core/helpers/create-http-params/create-http-params.helper';
import { ReviewStat } from '~/core/model/review-stats.model';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { APP_STORAGE } from '~/app.token';

const USER_ORGANIZATIONS = 'userOrganizations';

export interface OrganizationItem {
  organization: string;
  name: string;
  logo: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  protected organizationUrl: string;
  private readonly organizationsCache = new Map<
    string,
    Observable<OrganizationInterface>
  >();

  constructor(
    @Inject(ACTIVITIES_BASE_URL) private readonly baseUrl: string,
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly toggleService: ToggleService,
    protected readonly httpClient: HttpClient,
    protected readonly userService: UserService
  ) {
    this.organizationUrl = `${this.baseUrl}/organizations`;
  }

  public getOrganizationById(
    organizationId: string
  ): Observable<OrganizationInterface> {
    if (this.organizationsCache.has(organizationId)) {
      return this.organizationsCache.get(organizationId);
    }

    const OrganizationByIdRequest$ = this.httpClient
      .get<OrganizationInterface>(`${this.organizationUrl}/${organizationId}`)
      .pipe(
        tap((organization) => {
          const storageKey = `organization_${organizationId}`;
          const storageDateKey = `organization_${organizationId}_date`;
          this.storage?.setItem(storageKey, organization.name);
          this.storage?.setItem(storageDateKey, JSON.stringify(Date.now()));
        }),
        shareReplay(1)
      );

    this.organizationsCache.set(organizationId, OrganizationByIdRequest$);

    return OrganizationByIdRequest$;
  }

  public getOrganizationFromAuthUser(): Observable<OrganizationInterface | null> {
    const CURRENT_ORGANIZATION = 'currentOrganization';
    if (!this.storage.getItem(CURRENT_ORGANIZATION)) {
      return of(null);
    }

    const currentOrganization = JSON.parse(
      this.storage.getItem(CURRENT_ORGANIZATION)
    );

    const organizationFromAuthUser = currentOrganization?.organization?.replace(
      '/organizations/',
      ''
    );

    return this.getOrganizationIdsFromAuthUser().pipe(
      mergeMap((organizationIds) => {
        return organizationIds?.length === 0
          ? of(null)
          : this.getOrganizationById(organizationFromAuthUser);
      })
    );
  }

  public getOrganizationIdsFromAuthUser(): Observable<string[]> {
    return this.getOrganizationsFromAuthUser().pipe(
      map((organizationsByRole) => {
        const userOrganizations = JSON.stringify(organizationsByRole);
        const organizationIds: string[] = [];

        this.storage.setItem(USER_ORGANIZATIONS, userOrganizations);

        organizationsByRole.forEach((role: OrganizationsByRoleInterface) => {
          role.organizations.forEach((org) => {
            const organizationItem = org as unknown as OrganizationItem;
            const organizationId = organizationItem.organization.replace(
              '/organizations/',
              ''
            );
            organizationIds.push(organizationId);
          });
        });

        return organizationIds;
      })
    );
  }

  /** get reviews by organization id */
  public getOrganizationReviews(
    organizationId: string,
    filters?: StatfulReviewFiltersInterface
  ): Observable<ReviewInterface[]> {
    let params: HttpParams;

    if (filters) {
      params = createHttpParams(filters);
    }

    return this.httpClient
      .get<
        ReviewInterface[]
      >(`${this.organizationUrl}/${organizationId}/reviews`, { params })
      .pipe(catchError(() => of(null)));
  }

  /** get reviews stats by organization id */
  public getOrganizationReviewsStats(
    organizationId: string,
    filters?: StatfulReviewFiltersInterface
  ): Observable<ReviewStat> {
    let params: HttpParams;

    if (filters) {
      params = createHttpParams(filters);
    }

    return this.httpClient
      .get<ReviewStat>(
        `${this.organizationUrl}/${organizationId}/reviews-stats`,
        { params }
      )
      .pipe(
        map((reviewStatsData) => new ReviewStat(reviewStatsData)),
        catchError(() => of(null))
      );
  }

  public getOrganizationsFromAuthUser(): Observable<
    OrganizationsByRoleInterface[]
  > {
    return this.userService.getOrganizations();
  }

  public getStripeRedirectionUrl(
    organizationId: string,
    redirectUrl: string
  ): Observable<{ url: string }> {
    return this.httpClient
      .post<{
        url: string;
      }>(`${this.organizationUrl}/${organizationId}/payment-onboarding`, {
        redirectUrl,
      })
      .pipe(catchError(() => of(null)));
  }

  public getOrganizationOnboardingUrl(
    organizationId: string
  ): Observable<{ url: string }> {
    return this.httpClient.get<{ url: string }>(
      `${this.organizationUrl}/${organizationId}/onboarding`
    );
  }

  public checkOrganizationName(organizationId: string): Promise<string> {
    const storageKey = 'organization_' + organizationId;
    const storageDateKey = 'organization_' + organizationId + '_date';
    const organizationStorageData = this.storage?.getItem(storageKey);

    if (
      organizationStorageData?.length > 0 &&
      !this.timeToRefreshOrganizationsData(storageDateKey)
    ) {
      return Promise.resolve(organizationStorageData);
    } else {
      return Promise.resolve('');
    }
  }

  public timeToRefreshOrganizationsData(organizationKey): boolean {
    const organizationsStorageDataDate = JSON.parse(
      this.storage?.getItem(organizationKey)
    );
    const refreshDelay =
      Number(
        this.toggleService.getVariant(
          ToggleNameEnum.OrganizationsDataStorage,
          '24'
        )
      ) || 24;

    return (
      new Date().getTime() - organizationsStorageDataDate >
      refreshDelay * 60 * 60 * 1000
    );
  }
}
